import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AvisoApi,
    Notification,
} from 'app/layout/common/notifications/notifications.types';
import { abrirSolicitudes } from 'app/pages/tanda/admin-tanda/abrir-solicitudes';
import { environment } from 'environments/environment';
import { map, Observable, ReplaySubject, Subject, take, tap } from 'rxjs';

/**
 * Avisos de la persona.
 *
 * Lee **exactamente la misma bandeja que la app del teléfono** (`log_notifications`
 * del back), así que lo que se marca leído aquí queda leído allá y al revés. No
 * hay nada que sincronizar porque no hay dos copias: la campanita del navegador
 * y la del teléfono son dos ventanas al mismo renglón de la base.
 *
 * Antes esto apuntaba al API simulado que trae la plantilla de Fuse
 * (`api/common/notifications`), o sea que pintaba avisos de mentira.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);

    /** Pedidos de abrir el panel que llegan desde fuera de la campanita. */
    private _abrir = new Subject<void>();

    constructor(private _httpClient: HttpClient) {}

    /** La campanita escucha esto para abrir su panel. */
    get abrir$(): Observable<void> {
        return this._abrir.asObservable();
    }

    /**
     * Abre la bandeja desde otra pantalla (el dashboard la ofrece como "Ver
     * todos"). No hay pagina de avisos: la bandeja es el panel de la campanita,
     * y abrir ese mismo panel evita tener dos listas que se desincronicen.
     */
    abrirBandeja(): void {
        this._abrir.next();
    }

    /**
     * Los renglones tal como vienen del API, sin pasar por la forma de la
     * campanita. El dashboard los necesita crudos: agrupa por tanda y por tipo
     * (`comprobante_subido`, `solicitud_ingreso`...) para armar los pendientes.
     * No publica nada en `notifications$`.
     */
    getCrudos(take = 30): Observable<AvisoApi[]> {
        return this._httpClient
            .get<{ data: { notifications: AvisoApi[]; unread: number } }>(
                `${environment.apiUrl}/notifications`,
                { params: { take } }
            )
            .pipe(map((res) => res?.data?.notifications ?? []));
    }

    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    /** Trae la bandeja y la publica para la campanita. */
    getAll(): Observable<Notification[]> {
        return this._httpClient
            .get<{ data: { notifications: AvisoApi[]; unread: number } }>(
                `${environment.apiUrl}/notifications`,
                { params: { take: 30 } }
            )
            .pipe(
                map((res) => (res?.data?.notifications ?? []).map(aVista)),
                tap((notifications) => this._notifications.next(notifications))
            );
    }

    /**
     * Marca uno como leído.
     *
     * Sólo va en esa dirección: el API no sabe des-leer un aviso, y ofrecerlo
     * sería mentir. El botón de "marcar como no leído" que traía la plantilla
     * se quitó por eso.
     */
    markAsRead(id: string): Observable<Notification[]> {
        return this._httpClient
            .post<unknown>(`${environment.apiUrl}/notifications/read`, {
                ids: [id],
            })
            .pipe(
                map(() => this._marcarLocal((n) => n.id === id)),
                tap((notifications) => this._notifications.next(notifications))
            );
    }

    markAllAsRead(): Observable<Notification[]> {
        return this._httpClient
            .post<unknown>(`${environment.apiUrl}/notifications/read`, {})
            .pipe(
                map(() => this._marcarLocal(() => true)),
                tap((notifications) => this._notifications.next(notifications))
            );
    }

    /**
     * Pinta el cambio sin volver a pedir la lista entera.
     *
     * Es una lista de treinta renglones que la persona está mirando: recargarla
     * la haría parpadear y perdería el desplazamiento, por marcar algo que ya
     * sabemos cómo queda.
     */
    private _marcarLocal(coincide: (n: Notification) => boolean): Notification[] {
        let actuales: Notification[] = [];
        this.notifications$.pipe(take(1)).subscribe((n) => (actuales = n));

        return actuales.map((n) =>
            coincide(n) ? { ...n, read: true } : n
        );
    }
}

/** Traduce un renglón del API a lo que la campanita sabe pintar. */
function aVista(aviso: AvisoApi): Notification {
    return {
        id: aviso.idu_notification,
        icon: icono(aviso.cod_type),
        title: aviso.name_title,
        description: aviso.des_body,
        time: aviso.date_created,
        link: enlace(aviso),
        // La solicitud de ingreso abre directo esa solicitud (aviso nuevo, con
        // su id en la ruta) o, en avisos viejos, la lista de solicitudes.
        queryParams: aviso.cod_type === 'solicitud_ingreso' && enlace(aviso)
            ? abrirSolicitudes(solicitudDeLaRuta(aviso.cod_route))
            : undefined,
        useRouter: true,
        read: aviso.opc_read,
    };
}

/**
 * A dónde lleva el aviso.
 *
 * `cod_route` guarda un destino **lógico** (`tanda/<id>`) y no una URL, porque
 * la misma pantalla vive en direcciones distintas en la web y en la app. Aquí
 * se traduce, y la categoría decide a dónde: el corte de la ronda sólo le
 * llega a quien organiza, así que ese va al panel de administración; lo demás
 * (avisos de participante) va a Mis participaciones.
 */
const TIPOS_DEL_ORGANIZADOR = new Set(['comprobante_subido', 'solicitud_ingreso']);

function enlace(aviso: AvisoApi): string | undefined {
    // Un comprobante que llegó por WhatsApp se confirma en la bandeja, no en
    // el panel de la tanda: ahí están la imagen, la sugerencia y las señales.
    if (aviso.cod_type === 'comprobante_whatsapp') return '/tandaplus/comprobantes';

    const idu = aviso.idu_tanda ?? idDeLaRuta(aviso.cod_route);
    if (!idu) return undefined;

    // El comprobante subido y la solicitud de ingreso le llegan al organizador
    // aunque su categoría sea "movimientos" (es el interruptor de push que los
    // apaga): lo que tiene que hacer con ellos está en el panel, no en la
    // página pública.
    const esDelOrganizador =
        aviso.cod_category === 'organizador' || TIPOS_DEL_ORGANIZADOR.has(aviso.cod_type ?? '');
    // Al participante ("Arrancó la tanda"...) se le lleva a Mis participaciones,
    // dentro de su sesión y con su estado de cuenta; la página pública sacaba
    // del layout y, con la tanda iniciada, no le enseñaba lo suyo.
    return esDelOrganizador ? `/tandaplus/tanda/${idu}` : '/tandaplus/mis-participaciones';
}

/** `tanda/<id>` o `tanda/<id>/solicitudes/<idu_request>`: el id de la tanda. */
function idDeLaRuta(ruta: string | null): string | null {
    if (!ruta || !ruta.startsWith('tanda/')) return null;
    const id = ruta.slice('tanda/'.length).split('/')[0];
    return id.length > 0 ? id : null;
}

/** `tanda/<id>/solicitudes/<idu_request>`: la solicitud; null en avisos viejos. */
export function solicitudDeLaRuta(ruta: string | null): string | null {
    const partes = (ruta ?? '').split('/');
    if (partes.length < 4 || partes[0] !== 'tanda' || partes[2] !== 'solicitudes') return null;
    return partes[3] || null;
}

/**
 * Icono según lo que el aviso dice, no según su categoría: "te toca cobrar" y
 * "te falta aportar" son los dos de la familia `cobros` y son noticias
 * opuestas. Pintarlas igual obligaría a leer el texto para saber si hay que
 * preocuparse.
 */
function icono(tipo: string): string {
    switch (tipo) {
        case 'te_toca_cobrar':
            return 'heroicons_outline:gift';
        case 'pago_vencido':
            return 'heroicons_outline:exclamation-triangle';
        case 'pago_hoy':
        case 'pago_por_vencer':
            return 'heroicons_outline:clock';
        case 'corte_ronda':
            return 'heroicons_outline:clipboard-document-check';
        case 'comprobante_whatsapp':
            return 'heroicons_outline:chat-bubble-left-right';
        default:
            return 'heroicons_outline:bell';
    }
}
